import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Printer, ArrowLeft, MapPin, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

import { gerarRelatorioFotografico } from "@/lib/relatorioFotografico";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const fotoUrl = (apId, idx, w) => `${API_BASE}/api/public/apontamento/${apId}/foto/${idx}${w ? `?w=${w}` : ""}`;

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return d; }
}
function fmtDateTime(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; }
}
function fmtCoord(lat, lng) {
  if (lat == null || lng == null) return null;
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
}

export default function OrdemFotos() {
  const { id } = useParams();
  const nav = useNavigate();
  const [os, setOs] = useState(null);
  const [apontamentos, setApontamentos] = useState([]);
  const [itens, setItens] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [gerando, setGerando] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [modelo, setModelo] = useState("");
  const [modeloContrato, setModeloContrato] = useState("");

  useEffect(() => {
    (async () => {
      // Listagem leve: sem base64 das fotos (só a contagem); as miniaturas vêm sob demanda
      const [{ data: osData }, { data: aps }, { data: its }, { data: eqs }] = await Promise.all([
        api.get(`/ordens/${id}`),
        api.get(`/apontamentos/resumo?os_id=${id}&limit=500`),
        api.get(`/itens?os_id=${id}`),
        api.get(`/equipes`),
      ]);
      setOs(osData);
      setApontamentos(aps?.items || []);
      setItens(its || []);
      setEquipes(eqs || []);
      try {
        const { data: md } = await api.get("/relatorios-fotograficos/modelos");
        setModelos(md?.modelos || []);
        let padrao = md?.padrao || "padrao";
        if (osData?.contrato_id) {
          const { data: ct } = await api.get(`/contratos/${osData.contrato_id}`).catch(() => ({ data: null }));
          if (ct?.modelo_relatorio_fotografico) padrao = ct.modelo_relatorio_fotografico;
        }
        setModeloContrato(padrao); setModelo(padrao);
      } catch { /* mantém padrão do ERP */ }
    })();
  }, [id]);

  const [progresso, setProgresso] = useState(null);
  const baixarPdf = async () => {
    setGerando(true); setProgresso(null);
    try {
      const qs = new URLSearchParams(); if (dataInicio) qs.set("inicio", dataInicio); if (dataFim) qs.set("fim", dataFim); if (modelo) qs.set("modelo", modelo);
      await gerarRelatorioFotografico(`/ordens/${id}/fotos/pdf/jobs?${qs}`, `Relatorio_Fotografico_OS_${os?.numero || id}.pdf`, setProgresso);
      toast.success("PDF gerado");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Não foi possível gerar o PDF"); }
    finally { setGerando(false); setProgresso(null); }
  };


  const itemById = useMemo(() => Object.fromEntries(itens.map((i) => [i.id, i])), [itens]);
  const equipeById = useMemo(() => Object.fromEntries(equipes.map((e) => [e.id, e])), [equipes]);

  // Expande cada apontamento em 1 slot por foto. Só apontamentos COM foto.
  const slots = useMemo(() => {
    const out = [];
    const inLim = dataInicio ? new Date(dataInicio + "T00:00:00") : null;
    const fmLim = dataFim ? new Date(dataFim + "T23:59:59") : null;
    for (const a of apontamentos) {
      const nFotos = a.fotos_count || 0;
      if (nFotos === 0) continue;
      if (a.deleted) continue;
      const d = a.data_execucao ? new Date(a.data_execucao) : null;
      if (inLim && d && d < inLim) continue;
      if (fmLim && d && d > fmLim) continue;
      const it = itemById[a.item_id] || {};
      const eq = equipeById[a.equipe_id] || {};
      const ordemTipo = { antes: 0, durante: 1, depois: 2, verso: 3, carimbo: 4 };
      const idxOrd = Array.from({ length: nFotos }, (_, i) => i).sort((x, y) => (ordemTipo[(a.fotos_tipos || [])[x]] ?? 9) - (ordemTipo[(a.fotos_tipos || [])[y]] ?? 9) || x - y);
      for (const idx of idxOrd) {
        out.push({
          key: `${a.id}-${idx}`,
          foto: fotoUrl(a.id, idx, 800),
          fotoFull: fotoUrl(a.id, idx),
          tipo: { antes: "Antes", durante: "Durante", depois: "Depois", verso: "Verso", carimbo: "Carimbo" }[(a.fotos_tipos || [])[idx]] || "",
          data_execucao: a.data_execucao,
          servico: it.descricao || "—",
          categoria: it.categoria || "",
          unidade: it.unidade || "",
          quantidade: a.quantidade,
          equipe: eq.nome || "—",
          lat: a.latitude,
          lng: a.longitude,
          apontamento_obs: a.observacao || "",
        });
      }
    }
    // Ordenação: data ascendente (mais antigas primeiro)
    out.sort((x, y) => {
      const dx = x.data_execucao ? new Date(x.data_execucao).getTime() : 0;
      const dy = y.data_execucao ? new Date(y.data_execucao).getTime() : 0;
      return dx - dy;
    });
    return out;
  }, [apontamentos, itemById, equipeById, dataInicio, dataFim]);

  const resumo = useMemo(() => {
    const ids = new Set(apontamentos.filter((a) => (a.fotos_count || 0) > 0 && !a.deleted).map((a) => a.id));
    const datas = apontamentos
      .filter((a) => ids.has(a.id))
      .map((a) => a.data_execucao)
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
      .sort((a, b) => a - b);
    return {
      apontamentosComFoto: ids.size,
      fotosTotal: apontamentos.reduce((s, a) => s + (a.fotos_count || 0), 0),
      periodo: datas.length ? `${fmtDate(new Date(datas[0]))} – ${fmtDate(new Date(datas[datas.length - 1]))}` : "—",
    };
  }, [apontamentos]);

  if (!os) return <div style={{ padding: 32 }}>Carregando…</div>;

  // Pagina em grupos de 4 (2x2)
  const pages = [];
  for (let i = 0; i < slots.length; i += 4) pages.push(slots.slice(i, i + 4));

  return (
    <div className="pf-root" translate="no" lang="pt-BR" data-notranslate>
      <style>{`
        @media screen {
          .pf-root { max-width: 900px; margin: 24px auto; padding: 24px; background: #fff; }
          .pf-toolbar { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
          @page { margin: 0; }
        }
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: #FFF !important; }
          .no-print { display: none !important; }
          .pf-root { padding: 0; margin: 0; max-width: 100%; }
          .sidebar, .mobile-bottom-nav, .safety-stripes { display: none !important; }
          .app-shell { display: block !important; }
          .main-content { min-height: auto !important; }
          .pf-page { page-break-after: always; }
          .pf-page:last-child { page-break-after: auto; }
        }
        .pf-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0A0A0A; padding-bottom: 12px; margin-bottom: 16px; }
        .pf-logo { display: flex; align-items: center; gap: 12px; }
        .pf-logo-box { width: 44px; height: 44px; background: #FFCC00; display: flex; align-items: center; justify-content: center; font-weight: 900; font-family: 'Chivo', sans-serif; font-size: 1rem; }
        .pf-title { font-family: 'Chivo', sans-serif; font-weight: 900; font-size: 1.4rem; letter-spacing: -0.02em; text-transform: uppercase; margin: 0; line-height: 1; }
        .pf-sub { font-size: 0.72rem; color: #52525B; text-transform: uppercase; letter-spacing: 0.15em; margin-top: 4px; }
        .pf-badge { display: inline-block; padding: 4px 10px; background: #0A0A0A; color: #FFF; font-family: 'IBM Plex Mono', monospace; font-weight: 700; font-size: 1rem; }
        .pf-meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; font-size: 0.75rem; }
        .pf-meta-item { border-left: 3px solid #FFCC00; padding-left: 8px; }
        .pf-meta-label { text-transform: uppercase; letter-spacing: 0.1em; color: #71717A; font-size: 0.65rem; font-weight: 700; }
        .pf-meta-value { font-weight: 700; color: #0A0A0A; margin-top: 2px; word-break: break-word; }
        .pf-page { }
        .pf-grid { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 8mm; height: calc(297mm - 20mm - 90mm); }
        @media screen { .pf-grid { height: 800px; margin-bottom: 40px; } }
        .pf-cell { border: 1px solid #E4E4E7; display: flex; flex-direction: column; overflow: hidden; }
        .pf-img-wrap { flex: 1; background: #0A0A0A; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .pf-img { width: 100%; height: 100%; object-fit: contain; }
        .pf-caption { padding: 6px 8px; background: #FAFAFA; border-top: 1px solid #E4E4E7; font-size: 0.68rem; line-height: 1.35; }
        .pf-caption-date { font-family: 'IBM Plex Mono', monospace; font-weight: 700; color: #0A0A0A; }
        .pf-caption-servico { font-weight: 700; color: #0A0A0A; margin-top: 2px; }
        .pf-caption-detail { color: #52525B; margin-top: 2px; }
        .pf-caption-coord { color: #0A0A0A; margin-top: 2px; display: inline-flex; align-items: center; gap: 4px; }
        .pf-caption-coord a { color: #0A0A0A; text-decoration: underline; }
        .pf-empty { padding: 40px; text-align: center; color: #71717A; border: 2px dashed #E4E4E7; }
      `}</style>

      {/* Toolbar (só na tela) */}
      <div className="pf-toolbar no-print">
        <button className="btn" onClick={() => nav(-1)}><ArrowLeft size={16} /> Voltar</button>
        <button className="btn btn-primary" onClick={baixarPdf} disabled={gerando} data-testid="pf-baixar-pdf"><FilePdf size={16} weight="bold" /> {gerando ? (progresso?.status === "montando" ? `Montando PDF… ${progresso.montadas || 0}/${progresso.total || "?"}` : progresso?.total ? `Gerando… ${progresso.feitas || 0}/${progresso.total} fotos` : "Gerando PDF…") : "Baixar PDF"}</button>
        <button className="btn" onClick={() => window.print()} data-testid="pf-imprimir" title="Impressão do navegador (arquivo maior)"><Printer size={16} /> Imprimir</button>
        {modelos.length > 0 && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem" }}>
            <span className="label" style={{ margin: 0 }}>Modelo</span>
            <select className="input" value={modelo} onChange={(e) => setModelo(e.target.value)} data-testid="pf-modelo" title={modelos.find((m) => m.id === modelo)?.descricao || ""}>
              {modelos.map((m) => <option key={m.id} value={m.id}>{m.nome}{m.id === modeloContrato ? " (padrão do contrato)" : ""}</option>)}
            </select>
          </label>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <label className="label" style={{ margin: 0 }}>De</label>
          <input type="date" className="input" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} data-testid="pf-data-inicio" />
          <label className="label" style={{ margin: 0 }}>Até</label>
          <input type="date" className="input" value={dataFim} onChange={(e) => setDataFim(e.target.value)} data-testid="pf-data-fim" />
          {(dataInicio || dataFim) && (
            <button className="btn btn-ghost" onClick={() => { setDataInicio(""); setDataFim(""); }}>Limpar</button>
          )}
        </div>
      </div>

      {/* Cabeçalho + resumo (aparece uma vez, no topo do documento) */}
      <div className="pf-header">
        <div className="pf-logo">
          <div className="pf-logo-box">OS</div>
          <div>
            <h1 className="pf-title">Relatório Fotográfico</h1>
            <div className="pf-sub">OSTecnoVias · MA Tecnovias</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="pf-badge">{os.numero || "—"}</div>
          <div className="pf-sub" style={{ marginTop: 6 }}>Emitido em {fmtDate(new Date())}</div>
        </div>
      </div>

      <div className="pf-meta">
        <div className="pf-meta-item"><div className="pf-meta-label">Contratante</div><div className="pf-meta-value">{os.contratante || "—"}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Rodovia</div><div className="pf-meta-value">{os.rodovia || "—"}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Trecho</div><div className="pf-meta-value">{os.trecho || "—"}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Município</div><div className="pf-meta-value">{os.municipio || "—"}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Apontamentos c/ foto</div><div className="pf-meta-value">{resumo.apontamentosComFoto}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Total de fotos</div><div className="pf-meta-value">{slots.length}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Período</div><div className="pf-meta-value">{resumo.periodo}</div></div>
        <div className="pf-meta-item"><div className="pf-meta-label">Status OS</div><div className="pf-meta-value">{os.status || "—"}</div></div>
      </div>

      {slots.length === 0 ? (
        <div className="pf-empty" data-testid="pf-empty">Nenhum apontamento com foto encontrado{(dataInicio || dataFim) ? " no período selecionado" : ""}.</div>
      ) : (
        pages.map((group, pageIdx) => (
          <div className="pf-page" key={pageIdx} data-testid={`pf-page-${pageIdx}`}>
            <div className="pf-grid">
              {group.map((s) => {
                const coord = fmtCoord(s.lat, s.lng);
                return (
                  <div className="pf-cell" key={s.key}>
                    <div className="pf-img-wrap">
                      <a href={s.fotoFull} target="_blank" rel="noopener noreferrer" style={{ display: "contents" }}><img className="pf-img" src={s.foto} alt="foto do apontamento" loading="lazy" /></a>
                    </div>
                    <div className="pf-caption">
                      <div className="pf-caption-date">{fmtDateTime(s.data_execucao)}</div>
                      <div className="pf-caption-servico">{s.tipo && <span style={{ background: "#0A0A0A", color: "#FFF", padding: "1px 6px", borderRadius: 3, fontSize: "0.7rem", marginRight: 6, verticalAlign: "middle" }} data-testid="pf-tipo-foto">{s.tipo.toUpperCase()}</span>}{s.servico}</div>
                      <div className="pf-caption-detail">
                        {s.quantidade != null ? `${s.quantidade} ${s.unidade || ""}` : ""}
                        {s.equipe && s.equipe !== "—" ? ` · ${s.equipe}` : ""}
                      </div>
                      {coord && (
                        <div className="pf-caption-coord">
                          <MapPin size={11} weight="fill" />
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`}
                            target="_blank" rel="noopener noreferrer"
                          >{coord}</a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Preenche células vazias para manter grade 2x2 na última página */}
              {group.length < 4 && Array.from({ length: 4 - group.length }).map((_, i) => (
                <div className="pf-cell" key={`pad-${pageIdx}-${i}`} style={{ borderStyle: "dashed", opacity: 0.35 }} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
